import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, fromEvent, merge, Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs/operators";
import { CairaAuthService } from "../caira-ready/caira-auth.service";

/**
 * Why the gate is up, so it can say the right thing:
 *   "sign-in"    — pre-login, the general case.
 *   "live-class" — signed in, but on the in-app Zoom route, which needs a real
 *                  screen more than anything else in the LMS.
 *   "small-window" — the DEVICE is fine, the WINDOW is not. A computer with a
 *                  narrow window, or the page zoomed well in. Telling these
 *                  students to "open this on a tablet or desktop" is nonsense —
 *                  they are on one — and they got stuck, which is what this
 *                  reason exists to stop.
 */
export type DeviceGateReason = "sign-in" | "live-class" | "small-window";

/** Viewport size, published so the gate can show the student the actual numbers. */
export interface ViewportSize {
  width: number;
  height: number;
}

/**
 * Decides whether the viewport is too small to run the LMS.
 *
 * The LMS is designed for tablet and up. Rather than degrade badly on a phone —
 * or worse, let someone type an OTP and log in only to meet a layout they can't
 * use — a small screen gets a "switch to a tablet or desktop" screen instead.
 *
 * MOSTLY PRE-LOGIN. An already-signed-in session is otherwise left alone: the
 * point is to stop people investing time in a sign-in that leads nowhere, not to
 * lock out someone mid-session who picked up their phone.
 *
 * THE ONE EXCEPTION is the live class (/zoom/join), which is gated signed in or
 * not. The Zoom Meeting SDK needs the room even more than the rest of the LMS,
 * and letting a phone in produces the worst outcome available: the student
 * appears in the host's attendee list, believes they have joined, and sees
 * nothing usable. Gating it also tears the SDK down rather than leaving a ghost
 * attendee behind.
 */
@Injectable({ providedIn: "root" })
export class DeviceGateService {
  /**
   * Narrowest supported viewport: tablet portrait (768px), which is also this
   * project's existing `md` breakpoint.
   */
  static readonly MIN_WIDTH_PX = 768;

  /**
   * Shortest supported viewport. Catches phones held in landscape, which are
   * wide enough to pass the width test (a large phone is ~930px across) but far
   * too short to show a page of the LMS.
   */
  static readonly MIN_HEIGHT_PX = 480;

  /** Ignore the storm of resize events a drag or a rotation fires. */
  private static readonly RESIZE_DEBOUNCE_MS = 150;

  /**
   * Routes gated on a small screen even when signed in. Only the live class:
   * everything else in the LMS is reading, and a signed-in user glancing at it
   * on a phone is their business.
   *
   * Anchored and terminated so it matches /zoom/join and its query string but
   * not, say, /zoom/ended — that one is a plain "you left the class" message
   * that reads fine anywhere.
   */
  private static readonly LIVE_CLASS_ROUTE = /^\/zoom\/join(\/|\?|$)/;

  private readonly reasonSubject = new BehaviorSubject<DeviceGateReason | null>(
    null,
  );

  private readonly viewportSubject = new BehaviorSubject<ViewportSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  /** Live viewport size, so the "make the window bigger" screen can show the
   *  real numbers and shrink the gap as they drag. */
  readonly viewport$: Observable<ViewportSize> = this.viewportSubject
    .asObservable()
    .pipe(
      distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height),
    );

  /** What the window has to reach, for the copy. */
  readonly minWidth = DeviceGateService.MIN_WIDTH_PX;
  readonly minHeight = DeviceGateService.MIN_HEIGHT_PX;

  /** Why the gate is up, or null when it isn't. */
  readonly reason$: Observable<DeviceGateReason | null> = this.reasonSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  /** True while the screen is too small to continue. */
  readonly blocked$: Observable<boolean> = this.reason$.pipe(
    map((reason) => reason !== null),
    distinctUntilChanged(),
  );

  constructor(private cairaAuth: CairaAuthService, private router: Router) {
    // Seeded here rather than in the field initializer above: `cairaAuth` is a
    // constructor parameter and isn't assigned until the constructor body runs.
    this.refresh();

    // Rotating the device or resizing the window has to move the gate both ways.
    merge(
      fromEvent(window, "resize"),
      fromEvent(window, "orientationchange"),
    )
      .pipe(debounceTime(DeviceGateService.RESIZE_DEBOUNCE_MS))
      .subscribe(() => this.refresh());

    // Re-check on navigation: it is what raises the gate when a signed-in user
    // opens the live class, and what catches a session expiring mid-use — the
    // auth interceptor bounces those to /login, which a phone can't complete.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.refresh());
  }

  /** Recompute now. Called on resize, rotation and navigation. */
  refresh(): void {
    this.viewportSubject.next({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    this.reasonSubject.next(this.evaluate());
  }

  private evaluate(): DeviceGateReason | null {
    const tooSmall =
      window.innerWidth < DeviceGateService.MIN_WIDTH_PX ||
      window.innerHeight < DeviceGateService.MIN_HEIGHT_PX;
    if (!tooSmall) {
      return null;
    }

    const signedIn = this.cairaAuth.isLoggedIn();

    // Signed in, and not the live class: their business, no gate.
    if (signedIn && !DeviceGateService.LIVE_CLASS_ROUTE.test(this.router.url)) {
      return null;
    }

    // The window is too small but the MACHINE is not — a resizable window on a
    // real screen. Checked before the device copy, because "open this on a
    // tablet or desktop" is unactionable advice for someone already on one.
    if (this.isResizableWindowOnLargeScreen()) {
      return "small-window";
    }

    return signedIn ? "live-class" : "sign-in";
  }

  /**
   * The screen can hold the LMS even though the window currently cannot.
   *
   * `screen.availWidth/Height` is the DISPLAY, not the window, so it stays large
   * when someone drags a narrow window or zooms the page in — the two cases that
   * were being told to change device. A phone fails on the numbers (a large one
   * is ~430 across, ~930 in landscape but only ~430 tall).
   *
   * The pointer test is what separates a tablet from a laptop: a 10" tablet has
   * a display big enough to pass, but nothing there can be resized, so the
   * device copy is the correct advice. `any-pointer: fine` is true for a mouse,
   * trackpad or stylus and false on a touch-only device. A 2-in-1 with both
   * reports fine as well, which is right — it has a window to resize.
   */
  private isResizableWindowOnLargeScreen(): boolean {
    const screen = window.screen;
    const displayFits =
      !!screen &&
      screen.availWidth >= DeviceGateService.MIN_WIDTH_PX &&
      screen.availHeight >= DeviceGateService.MIN_HEIGHT_PX;
    if (!displayFits) {
      return false;
    }
    // No matchMedia (very old browsers): fall back to trusting the display
    // size, which is the more useful of the two answers here.
    if (typeof window.matchMedia !== "function") {
      return true;
    }
    return window.matchMedia("(any-pointer: fine)").matches;
  }
}
